const GA_MEASUREMENT_ID = 'G-YY0306L5MG';

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

/**
 * Loads gtag.js and configures GA4 (G-YY0306L5MG), mirroring the legacy
 * Tornado snippet in frontend/templates/layouts/master.html. Runtime-injected
 * (no inline script) so the build-time CSP hashing in vite-plugins/csp.ts is
 * unaffected; only SCRIPT_HOSTS/CONNECT_HOSTS entries are needed there.
 *
 * `send_page_view: false` — page_views are sent explicitly from the router's
 * onResolved subscription (see onRouteResolved) so the initial load and SPA
 * navigations share one code path without double-counting.
 */
function initAnalytics(enabled: boolean = import.meta.env.PROD): void {
  if (!enabled || window.gtag) return;

  window.dataLayer = window.dataLayer ?? [];
  function gtag(..._args: unknown[]): void {
    // gtag.js requires the Arguments object, not an array
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments);
  }
  window.gtag = gtag;

  gtag('js', new Date());
  gtag('config', GA_MEASUREMENT_ID, { send_page_view: false });

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);
}

interface PageViewLocation {
  pathname: string;
  searchStr: string;
}

function trackPageView(location: PageViewLocation): void {
  if (!window.gtag) return; // dev / disabled
  window.gtag('event', 'page_view', {
    page_path: `${location.pathname}${location.searchStr}`,
    page_location: window.location.href,
    page_title: document.title,
  });
}

/**
 * router.subscribe('onResolved') listener. The initial load has fromLocation
 * undefined and is always tracked; afterwards, resolutions that don't change
 * pathname or search (e.g. hash-only) are skipped.
 */
function onRouteResolved(event: {
  toLocation: PageViewLocation;
  fromLocation?: PageViewLocation;
}): void {
  const { toLocation, fromLocation } = event;
  if (
    fromLocation &&
    fromLocation.pathname === toLocation.pathname &&
    fromLocation.searchStr === toLocation.searchStr
  ) {
    return;
  }
  trackPageView(toLocation);
}

export { initAnalytics, trackPageView, onRouteResolved };
